import {Component, OnInit} from '@angular/core'
import {PageEvent} from '@angular/material/paginator'
import {ActivatedRoute, Router} from '@angular/router'
import {Store} from '@ngrx/store'
import {Observable} from 'rxjs'
import {ServiceUrlConstants} from 'src/constants/service-url-constants'
import {ExpenseReportAPIModel, ListExpenseAPIRequest, PaginatedAPI,} from 'src/models/api.model'
import {CommonService} from 'src/services/common/common.service'
import {DocumentService} from 'src/services/common/document.service'
import {AppState} from 'src/store/state/app.state'
import {ColumnConfig} from '../common/table/table.component'
import {ExpenseService} from 'src/services/expense/expense.service'
import {FETCH_EXPENSE_REPORT_LIST} from 'src/store/action/expense.action'
import {TableService} from "../../services/table.service";
import {FilterProps, FilterType} from "../common/filter-panel/filter-panel.component";

@Component({
  selector: 'app-expense-report',
  templateUrl: './expense-report.component.html',
  styleUrls: ['./expense-report.component.scss']
})
export class ExpenseReportComponent implements OnInit {
  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private appState: Store<AppState>,
    private cs: CommonService,
    private expenseService: ExpenseService,
    private documentService: DocumentService,
    private tableService: TableService
  ) {}

  data$: Observable<PaginatedAPI<ExpenseReportAPIModel>>

  filters: { [key: string]: string[] | string } = {};
  filterProps: FilterProps[] = [
    {
      field: 'batchDate',
      label: 'Batch Date',
      type: FilterType.DATE,
    },
    {
      field: 'runNumber',
      label: 'Run Number',
      type: FilterType.TEXT,
    },
  ]

  expenseReportAPIPayload: ListExpenseAPIRequest = {
    filters: {},
    sort: {
      sortBy: 'createdDatetime',
      sortOrder: 'DESC'
    },
    pageInfo: { pageNo: 0, limit: 5 }
  }

  columns: ColumnConfig<ExpenseReportAPIModel>[] = [
    {
      columnDef: 'batchDate',
      type: 'DATE',
      header: 'Batch Date',
      cell: (element: ExpenseReportAPIModel) => this.cs.getDateFromDateTime(element.batchDate),
      sortable: true,
      filterable: false,
    },
    {
      columnDef: 'runNumber',
      type: 'TEXT',
      header: 'Run Number',
      cell: (element: ExpenseReportAPIModel) => element.runNumber,
      sortable: true,
      filterable: false,
    },
    {
      columnDef: 'expenseCount',
      type: 'TEXT',
      header: 'Expense count',
      cell: (element: ExpenseReportAPIModel) => `<a class="html-anchor-cell" data-action="view-expenses" data-id="${element.runNumber}">${element?.expenseCount}</a>`,
      sortable: false,
      filterable: false,
      isHtml: true
    },
    {
      columnDef: 'statuses',
      type: 'TEXT',
      header: 'Distinct Expense Status',
      cell: (element: ExpenseReportAPIModel) => {
        let html = '';
        Object.entries(element?.expenseStatusCount ? element?.expenseStatusCount : []).forEach(([status, count]) => {
          html += `${this.tableService.buildStatusChipForExpReport(status, count)}<br />`;
        });
        return html;
      },
      isHtml: true
    },
    {
      columnDef: 'currencyTotals',
      type: 'TEXT',
      header: 'Currency Groupings',
      cell: (element: ExpenseReportAPIModel) => this.tableService.buildGroupingDisplay(element.currencyTotals, 'view-expenses-for-currency-groups'),
      isHtml: true,
      align: 'right',
      verticalAlign: 'top'
    },
    {
      columnDef: 'entityGroupings',
      type: 'TEXT',
      header: 'Entity Groupings',
      cell: (element: ExpenseReportAPIModel) => this.tableService.buildGroupingDisplay(element.entityTotals, 'view-expenses-for-entity-groups'),
      isHtml: true,
      align: 'right',
      verticalAlign: 'top'
    },
    {
      columnDef: 'viewExecutionLog',
      type: 'TEXT',
      header: 'View Execution Log',
      cell: (element: ExpenseReportAPIModel) => `<a class="html-anchor-cell" data-action="view-execution-log" data-id="${element.runNumber}">View Execution Log</a>`,
      sortable: false,
      filterable: false,
      isHtml: true
    },
    {
      columnDef: 'download',
      type: 'TEXT',
      header: 'Download SAEv3 Report',
      cell: (element: ExpenseReportAPIModel) => {
        return `<a class="html-anchor-cell" data-action="download-sae-file" data-id="${element.jobId}">Download SAEv3 Report</a>`
      },
      sortable: false,
      filterable: false,
      isHtml: true,
      width: 160
    }
  ]

  ngOnInit() {
    const runNumber = this.route.snapshot.queryParamMap.get('runNumber');

    if(runNumber) {
      this.filters['runNumber'] = runNumber
    }

    this.fetchExpenseReportList()
    this.data$ = this.expenseService.getExpenseReportFromStore();
  }

  onPageChangeExpenseReport(event: PageEvent) {
    this.expenseReportAPIPayload = {
      ...this.expenseReportAPIPayload,
      pageInfo: {
        pageNo: event.pageIndex,
        limit: event.pageSize
      }
    }
    this.fetchExpenseReportList()
  }

  onSortChangeExpenseReport(event: { active: string; direction: string }) {
    if (!event.active || !event.direction) {
      return
    }
    this.expenseReportAPIPayload = {
      ...this.expenseReportAPIPayload,
      sort: {
        sortBy: event.active,
        sortOrder: event.direction
      }
    }
    this.fetchExpenseReportList()
  }

  handleCellClick(event: { column: string; element: any; action: string; context?: string }) {
    const action = event.action
    switch (action) {
      case "download-sae-file": {
        this.downloadFile(event.element.runNumber)
        break
      }
      case "view-expenses": {
        let status = event.context;
        const runNumber = event.element.runNumber
        this.router.navigate(['/expenses'], { queryParams: { runNumber, status } });
        break
      }
      case "view-expenses-for-currency-groups": {
        let currency = event.context;
        const runNumber = event.element.runNumber
        this.router.navigate(['/expenses'], { queryParams: { runNumber, currency } });
        break
      }
      case "view-expenses-for-entity-groups": {
        let entity = event.context;
        const runNumber = event.element.runNumber
        this.router.navigate(['/expenses'], { queryParams: { runNumber, entity } });
        break
      }
      case "view-execution-log": {
        const runNumber = event.element.runNumber
        this.router.navigate(['/execution-logs'], { queryParams: { runNumber } });
        break
      }
    }
  }

  onFilterChange(filters: { [key: string]: string[] | string }) {
    this.filters = filters;
    this.fetchExpenseReportList()
  }

  fetchExpenseReportList() {
    this.expenseReportAPIPayload = {
      ...this.expenseReportAPIPayload,
      filters: this.filters
    }
    this.appState.dispatch(FETCH_EXPENSE_REPORT_LIST({ payload: this.expenseReportAPIPayload }))
  }

  downloadFile(id: string) {
    const endPoint = ServiceUrlConstants.API_FILE_EXPENSE_REPORT_DOWNLOAD.replace('{0}', id)
    this.documentService.downloadDocument(endPoint)
  }
}
